import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle, ArrowDown, ArrowUp, CalendarDays, FileText, History, Lock, Pencil,
  Trash2, Users,
} from 'lucide-react';
import { PERMISSIONS, TASK_TIMELINE_LIMIT } from '@pms/shared';
import type {
  PriorityLimitDetails, TaskOccurrenceAssignee, TaskTimelineEvent,
} from '@pms/shared';
import { Button } from '@/components/ui/button';
import { Tabs } from '@/components/ui/tabs';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { cn } from '@/lib/utils';
import { formatDate, formatDateTime, formatRelativeTime } from '@/lib/dateFormat';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { tasksService } from '@/services/tasks.service';
import { getErrorMessage, getPriorityLimitDetails } from '@/lib/errors';
import { SideDrawer } from './SideDrawer';
import { TaskStatusBadge, TaskTypeBadge } from './TaskBadges';
import { StatusDropdown } from './StatusDropdown';
import { AssigneeActionButton } from './AssigneeActionButton';
import { PriorityLimitDialog } from './PriorityLimitDialog';
import { fullName, initials, STATUS_META } from './taskConstants';
import { ManageAssigneesModal } from './ManageAssigneesModal';
import { EditTaskModal } from './EditTaskModal';
import { VoiceNotePlayer } from './VoiceNotePlayer';

interface TaskDetailDrawerProps {
  taskId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Read-only snapshot view (Report mode) — hides all actions. */
  readOnly?: boolean;
}

export function TaskDetailDrawer({ taskId, open, onOpenChange, readOnly = false }: TaskDetailDrawerProps) {
  const qc = useQueryClient();
  const { has } = usePermission();
  const { user, isSuperAdmin } = useAuth();
  const [tab, setTab] = useState<'details' | 'timeline'>('details');
  const [addOpen, setAddOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [priorityLimit, setPriorityLimit] = useState<PriorityLimitDetails | null>(null);

  const { data: task, isLoading, isError, refetch } = useQuery({
    queryKey: ['tasks', 'detail', taskId],
    queryFn: () => tasksService.get(taskId!),
    enabled: open && !!taskId,
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] });
    qc.invalidateQueries({ queryKey: ['tasks'] });
  }

  // One save applies the whole tick/untick diff. Adds go first so a task never
  // passes through a moment with nobody on it; removals are sequential because
  // each is its own endpoint.
  const saveAssignees = useMutation({
    mutationFn: async (v: { add: string[]; remove: string[] }) => {
      if (v.add.length) await tasksService.addAssignees(taskId!, { assignee_ids: v.add });
      for (const userId of v.remove) await tasksService.removeAssignee(taskId!, userId);
    },
    onSuccess: () => { toast.success('Assignees updated'); setAddOpen(false); invalidate(); },
    // Adding someone to a priority task can breach their cap; that gets the dialog naming
    // the blocking tasks. invalidate() either way — adds run before removals, so a
    // rejected add leaves the roster mid-change.
    onError: (e) => {
      const limit = getPriorityLimitDetails(e);
      if (limit) setPriorityLimit(limit);
      else toast.error(getErrorMessage(e));
      invalidate();
    },
  });

  // Latest occurrence's assignee rows drive the per-assignee status dropdowns.
  // Removing someone who had already started work keeps their occurrence row on
  // purpose (the service only deletes not-yet-started ones), so filter down to
  // people still on the task — otherwise they linger here after being taken off.
  const latestDate = task?.occurrence_assignees[0]?.occurrence_date;
  const onTask = new Set((task?.assignees ?? []).map((a) => a.user_id));
  const currentRows = (task?.occurrence_assignees ?? [])
    .filter((r) => r.occurrence_date === latestDate && onTask.has(r.user_id));
  const activeHolderId = currentRows.find((r) => r.is_active_holder)?.user_id ?? null;
  const chainLocked = currentRows.some((r) => r.holder_locked_at);

  const reorder = useMutation({
    mutationFn: (ids: string[]) => tasksService.reorderAssignees(taskId!, { assignee_ids: ids }),
    onSuccess: () => { toast.success('Sequence updated'); invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  function moveInChain(index: number, delta: number) {
    const ids = (task?.assignees ?? []).map((a) => a.user_id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    reorder.mutate(ids);
  }

  // Creating implies editing: only the creator may edit. SUPER_ADMIN bypasses every RBAC
  // check, per the project rule. task.edit deliberately does NOT appear here any more —
  // holding it must not grant edit on someone else's task. The server enforces the
  // identical rule in tasksService.updateTask().
  const isCreator = !!user && task?.created_by === user.id;

  const canAssign = has(PERMISSIONS.TASK_ASSIGN) && !readOnly;
  const canManage = has(PERMISSIONS.TASK_MANAGE) && !readOnly;
  const canEdit = !readOnly && (isSuperAdmin || isCreator);

  const deleteMutation = useMutation({
    mutationFn: () => tasksService.remove(task!.id),
    onSuccess: () => {
      toast.success('Task deleted');
      qc.invalidateQueries({ queryKey: ['tasks'] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Both dates are optional on a periodic task; one row covers them and disappears
  // when neither is set, rather than printing a line of em-dashes.
  const periodLabel = task?.task_type === 'periodic' && (task.periodic_start_date || task.periodic_end_date)
    ? `${task.periodic_start_date ? formatDate(task.periodic_start_date) : '—'} → ${task.periodic_end_date ? formatDate(task.periodic_end_date) : '—'}`
    : null;

  return (
    <SideDrawer open={open} onOpenChange={onOpenChange} title={task?.title ?? 'Task'}>
      {isLoading && (
        <div className="space-y-2 p-4 sm:p-5">
          {[...Array(6)].map((_, i) => <div key={i} className="h-10 animate-pulse rounded-md bg-muted/60" />)}
        </div>
      )}
      {isError && (
        <div className="p-6 text-center text-sm text-muted-foreground">
          Couldn’t load this task.{' '}
          <button className="text-primary underline" onClick={() => refetch()}>Retry</button>
        </div>
      )}
      {task && (
        <div>
          {/* Title + tabs stay put while the body scrolls. */}
          <div className="sticky top-0 z-10 border-b border-border/60 bg-background px-4 pb-2 pt-4 pr-12 sm:px-5 sm:pr-14">
            <div className="flex items-start gap-2">
              {task.is_priority && <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />}
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-base font-bold text-foreground sm:text-lg">{task.title}</h2>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <TaskTypeBadge type={task.task_type} />
                  {task.is_priority && (
                    <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 dark:bg-rose-500/15 dark:text-rose-400">Priority</span>
                  )}
                  {task.is_important && (
                    <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700 dark:bg-violet-500/15 dark:text-violet-400">Important</span>
                  )}
                  {/* Lifecycle, not occurrence status — the two are separate enums and
                      only the status one lost its 'stopped' member. */}
                  {task.lifecycle_state === 'stopped' && (
                    <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 dark:bg-rose-500/15 dark:text-rose-400">Stopped</span>
                  )}
                </div>
              </div>
            </div>

            <Tabs
              className="mt-3"
              active={tab}
              onChange={(k) => setTab(k as 'details' | 'timeline')}
              tabs={[
                { key: 'details', label: <span className="flex items-center gap-1.5"><FileText className="h-4 w-4" />Details</span> },
                { key: 'timeline', label: <span className="flex items-center gap-1.5"><History className="h-4 w-4" />History</span> },
              ]}
            />
          </div>

          <div className="p-4 sm:p-5">
            {tab === 'details' && (
              <div className="space-y-3">
                {/* Everything actionable in one row — no card per action. */}
                {(canManage || canAssign || canEdit) && (
                  <div className="flex flex-wrap gap-2">
                    {canEdit && (
                      <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
                        <Pencil className="h-4 w-4" /> Edit
                      </Button>
                    )}
                    {canAssign && (
                      <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
                        <Users className="h-4 w-4" /> Manage Assignees
                      </Button>
                    )}
                    {canManage && (
                      <Button size="sm" variant="outline" onClick={() => setConfirmDelete(true)}>
                        <Trash2 className="h-4 w-4 text-destructive" /> Delete
                      </Button>
                    )}
                  </div>
                )}

                {/* Created by, Period, then Description — one card rather than a separate
                    boxed Description block above, which cost a heading, a second border
                    and the gap between them. Type is already a badge in the header, and
                    the created / updated timestamps live in the Timeline tab. */}
                <div className="divide-y divide-border/40 rounded-lg border border-border/60 bg-card px-3">
                  <MetaRow label="Created by" value={fullName(task.created_by_first_name, task.created_by_last_name)} />
                  {periodLabel && <MetaRow label="Period" value={periodLabel} />}
                  {task.description && (
                    // Stacked, not a MetaRow: that truncates to one right-aligned line,
                    // which would hide most of a description.
                    <div className="py-2">
                      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Description</p>
                      <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-snug text-foreground">
                        {task.description}
                      </p>
                    </div>
                  )}
                </div>

                {task.voice_note_path && (
                  <VoiceNotePlayer src={`/tasks/${task.id}/voice-note`} />
                )}

                {task.is_important && (
                  <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-2.5 dark:border-violet-500/20 dark:bg-violet-500/5">
                    <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-violet-700 dark:text-violet-400">
                        Assignment Sequence
                      </p>
                      {chainLocked && (
                        <span className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
                          <Lock className="h-3 w-3" /> Locked — work started
                        </span>
                      )}
                    </div>
                    <div className="space-y-1">
                      {task.assignees.map((a, i) => (
                        <div
                          key={a.id}
                          className={cn(
                            'flex items-center gap-2 rounded-md border bg-card px-2 py-1.5',
                            a.user_id === activeHolderId ? 'border-emerald-400/70' : 'border-border/60',
                          )}
                        >
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-600 text-[10px] font-bold text-white">
                            {i + 1}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                            {fullName(a.first_name, a.last_name)}
                          </span>
                          <span
                            className={cn(
                              'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold',
                              a.is_punched_in
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400'
                                : 'bg-muted text-muted-foreground',
                            )}
                          >
                            {a.is_punched_in ? 'Punched in' : 'Not punched in'}
                          </span>
                          {a.user_id === activeHolderId && (
                            <span className="shrink-0 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white">
                              Current
                            </span>
                          )}
                          {canAssign && !chainLocked && (
                            <span className="flex shrink-0 items-center gap-0.5">
                              <button
                                type="button" aria-label="Move up"
                                disabled={i === 0 || reorder.isPending}
                                onClick={() => moveInChain(i, -1)}
                                className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted disabled:opacity-30"
                              >
                                <ArrowUp className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button" aria-label="Move down"
                                disabled={i === task.assignees.length - 1 || reorder.isPending}
                                onClick={() => moveInChain(i, 1)}
                                className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted disabled:opacity-30"
                              >
                                <ArrowDown className="h-3.5 w-3.5" />
                              </button>
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    {!activeHolderId && (
                      <p className="mt-1.5 text-xs font-medium text-rose-600 dark:text-rose-400">
                        Nobody in this sequence is punched in — the task is unassigned right now.
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Assignees ({task.assignees.length})
                    </p>
                    {latestDate && (
                      <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                        <CalendarDays className="h-3.5 w-3.5" /> {formatDate(latestDate)}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {currentRows.length === 0 && (
                      <p className="text-sm text-muted-foreground">No active occurrence for this task yet.</p>
                    )}
                    {currentRows.map((row) => (
                      <div key={row.id} className="rounded-lg border border-border/60 bg-card px-3 py-2">
                        <div className="flex items-center gap-2.5">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold">
                            {initials(row.first_name, row.last_name)}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{fullName(row.first_name, row.last_name)}</p>
                            {row.is_overdue && (
                              <div className="mt-0.5 flex flex-wrap items-center gap-1">
                                <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700 dark:bg-rose-500/15 dark:text-rose-400">Overdue</span>
                              </div>
                            )}
                          </div>
                          {(() => {
                            const isAssignee = row.user_id === user?.id;
                            // Creator branch wins when someone is both: the reviewer's
                            // controls are the superset, and a creator reviewing their
                            // own row is the existing self-assignment behaviour.
                            if (isCreator || isSuperAdmin) {
                              return (
                                <StatusDropdown
                                  row={row}
                                  taskType={task.task_type}
                                  onChanged={invalidate}
                                  disabled={readOnly}
                                />
                              );
                            }
                            if (isAssignee) {
                              // Badge alongside the button: the button says what to do
                              // next, the badge says where the task stands. The dropdown
                              // the creator gets already doubles as its own badge.
                              return (
                                <div className="flex shrink-0 items-center gap-2">
                                  <TaskStatusBadge status={row.status} />
                                  <AssigneeActionButton
                                    row={row}
                                    onChanged={invalidate}
                                    disabled={readOnly}
                                  />
                                </div>
                              );
                            }
                            // Neither party — show the badge only, no control at all.
                            return <TaskStatusBadge status={row.status} />;
                          })()}
                        </div>
                        <ReviewHistory events={task?.timeline ?? []} row={row} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {tab === 'timeline' && (
              <div className="rounded-lg border border-border/60 bg-card">
                <div className="flex items-center gap-2 border-b border-border/40 px-3 py-2 text-sm font-semibold text-primary">
                  <History className="h-4 w-4" /> Activity
                  {/* At the cap the real total is unknown without another query,
                      so say "latest N" rather than state a wrong total. */}
                  <span className="ml-auto text-xs font-normal text-muted-foreground">
                    {task.timeline.length >= TASK_TIMELINE_LIMIT
                      ? `latest ${TASK_TIMELINE_LIMIT}`
                      : `${task.timeline.length} ${task.timeline.length === 1 ? 'entry' : 'entries'}`}
                  </span>
                </div>
                {task.timeline.length === 0 ? (
                  <p className="px-3 py-6 text-center text-sm text-muted-foreground">No activity yet.</p>
                ) : (
                  <ul className="divide-y divide-border/40">
                    {task.timeline.map((ev) => (
                      <li key={ev.id} className="px-3 py-2">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="min-w-0 text-xs text-foreground">
                            <span className="font-semibold">
                              {ev.actor_id ? fullName(ev.actor_first_name, ev.actor_last_name) : 'System'}
                            </span>{' '}
                            <span className="text-muted-foreground">{timelineLabel(ev.event_type)}</span>
                          </span>
                          {/* Relative reads faster; the exact stamp stays on hover. */}
                          <span
                            className="shrink-0 text-[11px] text-muted-foreground"
                            title={formatDateTime(ev.created_at)}
                          >
                            {formatRelativeTime(ev.created_at)}
                          </span>
                        </div>
                        {ev.from_status && ev.to_status && (
                          <p className="mt-0.5 text-[11px] text-muted-foreground">
                            {STATUS_META[ev.from_status]?.label ?? ev.from_status} → {STATUS_META[ev.to_status]?.label ?? ev.to_status}
                          </p>
                        )}
                        {ev.note && (
                          <p className="mt-0.5 whitespace-pre-wrap text-[11px] text-muted-foreground">“{ev.note}”</p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {task && (
        <>
        {/* Mounted only while open: EditTaskModal seeds its assignee list from the task
            on mount, so a fresh mount is what re-syncs it after a save. */}
        {editOpen && (
          <EditTaskModal
            task={task}
            open
            onOpenChange={setEditOpen}
            onSaved={invalidate}
          />
        )}

        <ConfirmDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title="Delete this task?"
          description="The task and its occurrences will be removed. This cannot be undone."
          confirmLabel={deleteMutation.isPending ? 'Deleting…' : 'Yes, delete'}
          destructive
          onConfirm={() => deleteMutation.mutate()}
        />

        {/* Mounted only while open so each opening starts clean — no reset effect. */}
        {addOpen && (
          <ManageAssigneesModal
            onClose={() => setAddOpen(false)}
            busy={saveAssignees.isPending}
            current={task.assignees.map((a) => ({
              user_id: a.user_id, first_name: a.first_name, last_name: a.last_name,
            }))}
            isImportant={task.is_important}
            onSave={(add, remove) => saveAssignees.mutate({ add, remove })}
          />
        )}

        </>
      )}
      <PriorityLimitDialog details={priorityLimit} onClose={() => setPriorityLimit(null)} />
    </SideDrawer>
  );
}

/**
 * Every review round on one assignee's row, newest first.
 *
 * Filtered by `subject_user_id` — whose row changed — rather than `actor_id`, which is
 * the reviewer. Without that distinction an occurrence with several assignees would show
 * everyone's feedback on everyone's row.
 *
 * Rows written before subject_user_id existed carry NULL and are silently skipped: they
 * cannot be attributed to anyone, and guessing would be worse than omitting them.
 */
function ReviewHistory({ events, row }: { events: TaskTimelineEvent[]; row: TaskOccurrenceAssignee }) {
  const rounds = events.filter((e) =>
    e.subject_user_id === row.user_id
    && e.occurrence_id === row.occurrence_id
    && (e.to_status === 'needs_improvement' || e.to_status === 'completed'));

  if (rounds.length === 0) return null;

  return (
    <ul className="mt-2 space-y-1.5 border-t border-border/40 pt-2">
      {rounds.map((e) => (
        <li key={e.id} className="text-xs">
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <TaskStatusBadge status={e.to_status!} />
            <span className="text-muted-foreground">
              by {fullName(e.actor_first_name, e.actor_last_name)} · {formatRelativeTime(e.created_at)}
            </span>
          </div>
          {e.note && <p className="mt-0.5 whitespace-pre-wrap text-muted-foreground">{e.note}</p>}
          {/* The reviewer's spoken remark for THIS round. No label — the badge and
              timestamp above already say what it belongs to. */}
          {e.voice_note_path && (
            <div className="mt-1">
              <VoiceNotePlayer src={`/tasks/timeline/${e.id}/voice-note`} label={null} />
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Label left, value right — survives a 375px width where a 2-column grid does not. */
function MetaRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="shrink-0 text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right text-sm text-foreground">{value}</span>
    </div>
  );
}

function timelineLabel(type: string): string {
  switch (type) {
    case 'created': return 'created this task';
    case 'status_changed': return 'changed status';
    case 'assignee_added': return 'added an assignee';
    case 'assignee_removed': return 'removed an assignee';
    // Retired grace flow — nothing emits these now, but old rows still render.
    case 'grace_granted': return 'granted grace';
    case 'grace_requested': return 'asked for extra time';
    case 'grace_rejected': return 'refused extra time';
    case 'stopped': return 'stopped this task';
    case 'reactivated': return 'reactivated this task';
    case 'edited': return 'edited this task';
    case 'holder_changed': return 'reassigned along the sequence';
    case 'no_holder_available': return 'found nobody available in the sequence';
    case 'auto_missed': return 'closed this at the daily reset';
    case 'auto_archived': return 'archived this automatically';
    default: return type;
  }
}
